/**
 * Centralised, typed environment configuration.
 *
 * This module is the single place where `process.env.*` is read in the
 * application source. Other code MUST import from `src/config` rather than
 * touching `process.env` directly.
 *
 * Conventions:
 * - `requireEnv(name)` throws at module load if the variable is missing.
 * - `optionalEnv(name, fallback?)` returns `undefined` (or `fallback`) when
 *   the variable is missing, for variables that can legitimately be absent
 *   (e.g. analytics in a self-hosted fork).
 * - Variables intended for the browser bundle MUST be prefixed `REACT_APP_`.
 *
 * See `.env.example` for the full list of recognised variables.
 */

type Env = "PROD" | "REVIEW" | "DEV";

function readRawEnv(name: string): string | undefined {
	// `process.env` is statically inlined by webpack/CRA at build time for
	// `REACT_APP_*` and `NODE_ENV`. Treat empty strings as missing.
	const value = process.env[name];
	if (value === undefined || value === "") return undefined;
	return value;
}

function requireEnv(name: string): string {
	const value = readRawEnv(name);
	if (value === undefined) {
		throw new Error(
			`Missing required environment variable: ${name}. ` +
				"Set it in your .env file (see .env.example) or in your deployment configuration.",
		);
	}
	return value;
}

function optionalEnv(name: string): string | undefined;
function optionalEnv(name: string, fallback: string): string;
function optionalEnv(name: string, fallback?: string): string | undefined {
	return readRawEnv(name) ?? fallback;
}

const rawEnvironment = optionalEnv("REACT_APP_ENVIRONMENT", "DEV").toUpperCase();
const environment: Env =
	rawEnvironment === "PROD" || rawEnvironment === "REVIEW" ? rawEnvironment : "DEV";

const isProd = environment === "PROD";

/**
 * Pick a value based on the current environment. Used for the small number of
 * settings (asset bucket, embedded map iframe) where prod and review point at
 * different hosts but we don't want deployers to set both unless they need to.
 */
function pickByEnv<T extends string>(opts: { prod: T; nonProd: T }): T {
	return isProd ? opts.prod : opts.nonProd;
}

// -----------------------------------------------------------------------------
// API
// -----------------------------------------------------------------------------

/**
 * Base URL of the main backend API.
 *
 * In `npm start` (NODE_ENV=development), returns same-origin `/api/` so
 * `src/setupProxy.js` can forward to `REACT_APP_BASE_API_ROUTE` without CORS.
 * In production builds, returns the configured absolute URL.
 *
 * `REACT_APP_BASE_API_ROUTE` is still required in all environments — it tells
 * the dev proxy where to forward, and production where to call directly.
 *
 * Note: unit tests inject this via `src/test/dotenv.ts` so MSW handlers and
 * the RTK Query base URL agree.
 */
function resolveApiBaseUrl(): string {
	const configured = requireEnv("REACT_APP_BASE_API_ROUTE");
	if (process.env.NODE_ENV === "development") {
		return "/api/";
	}
	return configured;
}

export const apiBaseUrl = resolveApiBaseUrl();

/**
 * Derived public-API base URL (`<apiBaseUrl>/public/`). Kept here so the
 * derivation lives in one place.
 */
export const apiPublicBaseUrl = `${apiBaseUrl.replace(/\/$/, "")}/public/`;

// -----------------------------------------------------------------------------
// External services
// -----------------------------------------------------------------------------

/**
 * Base URL of the OpenEO Connector microservice (MSA calculation).
 * Optional: features that depend on it (BINTACT MSA flow) will throw at call
 * time if missing.
 */
export const msaServiceBaseUrl = optionalEnv("REACT_APP_MSA_BASE_URL");

/**
 * Base URL of the helper backend used for HiH raster painting / map save
 * status polling. Optional.
 */
export const helperBackendBaseUrl = optionalEnv("REACT_APP_HELPER_API_BASE_URL");

/**
 * Public assets bucket (icons, images, fonts served from CDN).
 * Two env vars so that prod and review hosts are configured independently.
 */
export const assetsBucketUrl = pickByEnv({
	prod: optionalEnv("REACT_APP_ASSETS_BUCKET_URL_PROD", ""),
	nonProd: optionalEnv("REACT_APP_ASSETS_BUCKET_URL_REVIEW", ""),
});

// -----------------------------------------------------------------------------
// Embedded maps
// -----------------------------------------------------------------------------

/**
 * URL of the embedded Ex-Act map iframe. Selected by environment.
 */
export const mapIframeUrl = pickByEnv({
	prod: optionalEnv("REACT_APP_MAP_URL_PROD", ""),
	nonProd: optionalEnv("REACT_APP_MAP_URL_DEV", ""),
});

/**
 * URL of the embedded BINTACT (Agro-Informatics) map iframe.
 * Single env var because BINTACT does not currently have a prod/non-prod
 * split in the UI.
 */
export const bintactMapUrl = optionalEnv("REACT_APP_BINTACT_MAP_URL", "");

/**
 * URL of the JSON config consumed by the embedded Ex-Act map. The same URL
 * is sent twice in `initSources` to keep parity with the map's expected
 * payload shape.
 */
export const mapConfigSourceUrl = optionalEnv(
	"REACT_APP_MAP_CONFIG_SOURCE_URL",
	"",
);

// -----------------------------------------------------------------------------
// Firebase analytics
// -----------------------------------------------------------------------------

/**
 * Firebase Web SDK configuration.
 *
 * Firebase Web API keys are public by design (Google enforces access via
 * Firebase Security Rules and authorised domains), so it is safe to ship
 * them in the bundle. They are still kept behind env vars so:
 *   1. OSS forks can plug in their own Firebase project without code changes.
 *   2. We can disable analytics in self-hosted deployments by simply not
 *      setting the variables.
 *
 * If `apiKey` or `projectId` are missing, the rest of the app continues to
 * work but `useFirebaseHook` becomes a no-op (see `src/utils/firebaseUtils.ts`).
 */
export const firebaseConfig = {
	apiKey: optionalEnv("REACT_APP_FIREBASE_API_KEY"),
	authDomain: optionalEnv("REACT_APP_FIREBASE_AUTH_DOMAIN"),
	projectId: optionalEnv("REACT_APP_FIREBASE_PROJECT_ID"),
	storageBucket: optionalEnv("REACT_APP_FIREBASE_STORAGE_BUCKET"),
	messagingSenderId: optionalEnv("REACT_APP_FIREBASE_MESSAGING_SENDER_ID"),
	appId: optionalEnv("REACT_APP_FIREBASE_APP_ID"),
	measurementId: optionalEnv("REACT_APP_FIREBASE_MEASUREMENT_ID"),
};

export const isFirebaseConfigured = Boolean(
	firebaseConfig.apiKey && firebaseConfig.projectId,
);
